import React, {useContext, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Grid, ListItem, ListItemIcon, ListItemText, Paper} from '@material-ui/core';
import {CheckBox as CheckBoxChecked, CheckBoxOutlineBlank as CheckBoxUnchecked} from '@material-ui/icons';
import {createForm, formShape} from 'rc-form';
import {isEmpty} from 'lodash/lang';
import {GreatForm} from 'components/common';
import AutoSuggestSelect from '../common/rc-fields/AutoSuggestSelect';
import WrapperField from '../common/rc-fields/WrapperField';
import {fetchAllClassroomReaders, fetchClassrooms, importStudentsToClassroom} from 'api/classrooms';
import {buildUserFullName} from 'utils/common';

const StudentImportForm = ({schoolId, classroomId, form, onSubmitSuccess}) => {
  const [readers, setReaders] = useState(null);
  const {classroom} = form.getFieldsValue();

  useEffect(() => {
    if (isEmpty(classroom)) return;
    Promise.all([fetchAllClassroomReaders(classroomId), fetchAllClassroomReaders(classroom.id)]).then(
      ([{records: currentReaders}, {records: readersToImport}]) => {
        const currentReaderIds = new Set(currentReaders.map((reader) => reader.id));
        setReaders(readersToImport.filter((reader) => !currentReaderIds.has(reader.id)));
      },
    );
  }, [classroom]);

  return (
    <GreatForm
      form={form}
      submitFn={(payload) => importStudentsToClassroom({classroomId, ...payload})}
      onSuccess={onSubmitSuccess}
      successMsg="Students successfully imported to the classroom"
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <AutoSuggestSelect
            name="classroom"
            label="Classroom"
            labelKey="name"
            valueKey="id"
            onSearch={(value) =>
              fetchClassrooms(value, undefined, undefined, undefined, {
                schoolId,
                includeArchived: true,
              }).then(({records}) => records)
            }
            rules={[{required: true, message: 'Please select a classroom'}]}
            form={form}
            autoComplete="off"
          />
        </Grid>
        {!isEmpty(classroom) && (
          <Grid item xs={12}>
            <WrapperField
              name="userIds"
              label="Students"
              initialValue={new Set()}
              rules={[
                {
                  validator(rule, value, callback) {
                    const errors = [];
                    if (value.size === 0) {
                      errors.push('Please select students to import');
                    }
                    callback(errors);
                  },
                },
              ]}
              form={form}
              render={({fieldProps: {value, onChange}}) =>
                isEmpty(readers) ? (
                  'No students in the selected classroom'
                ) : (
                  <Grid container spacing={2}>
                    {readers.map((reader) => {
                      const selected = value.has(reader.id);
                      return (
                        <Grid key={reader.id} item xs={12} md={6} lg={4}>
                          <ListItem
                            button
                            component={Paper}
                            onClick={() => {
                              const _value = new Set(value);
                              if (selected) {
                                _value.delete(reader.id);
                              } else {
                                _value.add(reader.id);
                              }
                              onChange(_value);
                            }}
                          >
                            <ListItemIcon>
                              {selected ? <CheckBoxChecked color="secondary" /> : <CheckBoxUnchecked />}
                            </ListItemIcon>
                            <ListItemText primary={buildUserFullName(reader)} />
                          </ListItem>
                        </Grid>
                      );
                    })}
                  </Grid>
                )
              }
            />
          </Grid>
        )}
      </Grid>
    </GreatForm>
  );
};

StudentImportForm.propTypes = {
  form: formShape.isRequired,
  schoolId: PropTypes.number.isRequired,
  classroomId: PropTypes.number.isRequired,
  onSubmitSuccess: PropTypes.func.isRequired,
};

StudentImportForm.defaultProps = {
  initialValues: {},
};

export default createForm()(StudentImportForm);
